/**
 * A FÁJLVÁLASZTÁS — egy rejtett `<input type="file">` és a gomb, ami megnyitja.
 *
 * MIÉRT KÉT BELÉPÉSI PONT (fénykép és fájl), és miért ebben a sorrendben. Az
 * ügyfél telefonon áll, és a makett (`design/Ugyfel.dc.html`) első, kiemelt
 * gombja a „Lefotózom" — a papír a kezében van. A fájlválasztás a MÁSODIK
 * út: azoknak, akiknek a számla már a gépükön/telefonjukon PDF.
 *
 * A `capture="environment"` az, ami mobilon a HÁTLAPI kamerát nyitja meg
 * közvetlenül, galéria helyett. Asztali böngésző figyelmen kívül hagyja, és
 * sima fájlválasztót ad — ezért nem kell külön ág rá.
 *
 * ⚠️ A `multiple` ÉS A `camera` EGYÜTT NEM MEGY, és nem is akarjuk. A
 * `capture` egy FELVÉTELT kér a kamerától; a kettő együtt böngészőnként
 * másképp viselkedik (van, ahol a `capture` nyer és egy fájl jön, van, ahol
 * a galéria nyílik meg). A hívó vagy fényképet kér, vagy köteget.
 *
 * A REJTETT INPUT NEM `display: none`. Egy `display: none` mezőre a
 * képernyőolvasó és a billentyűzet sem talál rá, és a `<label>`-es
 * megoldásnál a gomb szerepe vész el. Itt a `<button>` a vezérlő — az kap
 * fókuszt, azon van a felirat —, az input pedig a `.sr-only` osztállyal
 * marad elérhető.
 */

import { type ReactNode, useId, useRef } from 'react';
import { ACCEPT_ATTRIBUTE } from '@/contracts/generated/upload-limits';

interface FilePickerProps {
  label: string;
  /** Mobilon a hátlapi kamerát nyitja meg galéria helyett. Kizárja a `multiple`-t. */
  camera?: boolean;
  /** Csoportos feltöltés — több fájl egy kiválasztásban. */
  multiple?: boolean;
  /**
   * A `tile` a kérés-kártya ikon-csempéje (#57): ikon fölötte, felirat alatta.
   * Ugyanaz a gomb, csak más alakban — a szerepe nem változik.
   */
  variant?: 'primary' | 'secondary' | 'link' | 'tile';
  /** Dísz a felirat előtt; a gomb neve a `label` marad. */
  icon?: ReactNode;
  disabled?: boolean;
  /**
   * MINDIG LISTÁT KAP, egyetlen fájlnál is. Az egyelemű lista a hívó
   * oldalán megszünteti a „egy vagy több" elágazást — a `useUploader` úgyis
   * kötegre van felkészítve.
   */
  onPick: (files: File[]) => void;
}

export function FilePicker({
  label,
  camera = false,
  multiple = false,
  variant = 'primary',
  icon,
  disabled = false,
  onPick,
}: FilePickerProps) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);

  return (
    <>
      <button
        type="button"
        className={`pick pick-${variant}`}
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        {icon === undefined ? (
          label
        ) : (
          <>
            <span className="pick-icon">{icon}</span>
            <span>{label}</span>
          </>
        )}
      </button>
      <input
        id={inputId}
        ref={input}
        className="sr-only"
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        multiple={multiple && !camera}
        {...(camera ? { capture: 'environment' as const } : {})}
        aria-label={label}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) {
            onPick(files);
          }
          // ÜRÍTÉS A KIVÁLASZTÁS UTÁN. Enélkül ugyanannak a fájlnak az
          // ÚJRAVÁLASZTÁSA nem vált `change` eseményt (az érték nem
          // változott), és egy megszakadt feltöltés újrapróbálása némán nem
          // csinálna semmit — pont a leggyakoribb mobilos eset.
          event.target.value = '';
        }}
      />
    </>
  );
}
